import { Check, Close, Engineering, Person } from '@mui/icons-material';
import { Avatar, Box, CircularProgress, IconButton, ListItem, ListItemAvatar } from '@mui/material';
import { Form, Formik } from 'formik';
import { FC, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import * as yup from 'yup';

import { FormikEuroInput } from '../../../../../components/formikInputs/FormikEuroInput';
import { useAppSelector } from '../../../../../state/redux.hooks';
import { useMitarbeiterRole } from '../../../../auth/api/auth.hook';
import { selectMitarbeiterId } from '../../../../auth/api/authSlice';
import { MitarbeiterRole } from '../../../../auth/api/types';
import { FormikMitarbeiterAutocomplete } from '../../../../mitarbeiter/components/mitarbeiterAutocomplete/FormikMitarbeiterAutocomplete';
import { MitarbeiterLink } from '../../../../mitarbeiter/components/MitarbeiterLink';
import {
  useCreateProjektMitarbeiterMutation,
  useUpdateProjektMitarbeiterMutation,
} from '../../../api/projektApi';
import {
  CreateProjektMitarbeiterDTO,
  ProjektMitarbeiter,
  UpdateProjektMitarbeiterDTO,
} from '../../../api/projektMitarbeiter.types';

const emptyCreateMitarbeiterDTOInitialValues: CreateProjektMitarbeiterDTO = {
  mitarbeiterId: '',
  stundenSatzInEuroCent: 10000,
  projektId: '',
};

interface ProjectMitarbeiterFormProps {
  istPauschal: boolean;
  excludeMitarbeiterIdsFromAutocomplete?: string[];
  onClose: () => void;
  saveMode: 'create' | 'edit';
  isProjektleiter?: boolean;
  updateProjektMitarbeiterDTOinitialValues?: CreateProjektMitarbeiterDTO & { id: string };
  createProjektMitarbeiterDTOinitialValues?: Partial<CreateProjektMitarbeiterDTO>;
}

export const ProjektMitarbeiterForm: FC<ProjectMitarbeiterFormProps> = ({
  istPauschal,
  saveMode,
  isProjektleiter,
  excludeMitarbeiterIdsFromAutocomplete,
  createProjektMitarbeiterDTOinitialValues,
  onClose,
  updateProjektMitarbeiterDTOinitialValues,
}) => {
  const userRole = useMitarbeiterRole();
  const currentUserId = useAppSelector(selectMitarbeiterId);
  const isIntern = userRole === MitarbeiterRole.INTERN;

  const [createProjektMitarbeiter, { isLoading: createMitarbeiterLoading }] =
    useCreateProjektMitarbeiterMutation();
  const [updateProjektMitarbeiter, { isLoading: updateMitarbeiterLoading }] =
    useUpdateProjektMitarbeiterMutation();

  const loading = createMitarbeiterLoading || updateMitarbeiterLoading;
  const modeIsCreate = saveMode === 'create';

  const validationSchema = useMemo(
    () =>
      yup.object({
        mitarbeiterId:
          isIntern && modeIsCreate ? yup.string().optional() : yup.string().required('notwendig'),
        projektId: yup.string().required('notwendig'),
        stundenSatzInEuroCent: istPauschal
          ? yup.number().optional()
          : yup.number().min(1, 'notwendig').required('notwendig'),
      }),
    [isIntern, modeIsCreate, istPauschal]
  );

  const handleSave = useCallback(
    (
      dto: CreateProjektMitarbeiterDTO | UpdateProjektMitarbeiterDTO
    ): Promise<ProjektMitarbeiter> =>
      modeIsCreate
        ? createProjektMitarbeiter(dto as CreateProjektMitarbeiterDTO).unwrap()
        : updateProjektMitarbeiter(dto as UpdateProjektMitarbeiterDTO).unwrap(),
    [createProjektMitarbeiter, updateProjektMitarbeiter, modeIsCreate]
  );

  const initialValues: CreateProjektMitarbeiterDTO | UpdateProjektMitarbeiterDTO = useMemo(
    () =>
      modeIsCreate
        ? {
            ...emptyCreateMitarbeiterDTOInitialValues,
            stundenSatzInEuroCent: istPauschal ? 0 : 10000,
            ...(isIntern && currentUserId ? { mitarbeiterId: currentUserId } : {}),
            ...createProjektMitarbeiterDTOinitialValues,
          }
        : { ...updateProjektMitarbeiterDTOinitialValues },
    [
      createProjektMitarbeiterDTOinitialValues,
      updateProjektMitarbeiterDTOinitialValues,
      modeIsCreate,
      istPauschal,
      isIntern,
      currentUserId,
    ]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={dto => {
        const payload = { ...dto };
        if (isIntern && modeIsCreate && !payload.mitarbeiterId) {
          delete payload.mitarbeiterId;
        }
        return handleSave(payload)
          .then(() => {
            onClose();
            toast.success(
              modeIsCreate
                ? 'Projektmitarbeiter erfolgreich hinzugefügt!'
                : 'Projektmitarbeiter erfolgreich bearbeitet!'
            );
          })
          .catch((error: unknown) => {
            const apiErr = error as { status?: number; data?: { message?: string | string[] } };
            const msg = Array.isArray(apiErr?.data?.message)
              ? apiErr.data.message[0]
              : apiErr?.data?.message || 'Fehler beim Speichern des Projektmitarbeiters';
            toast.error(msg);
          });
      }}
    >
      {({ submitForm }) => (
        <Form>
          <ListItem
            sx={{ pb: 3, pt: !modeIsCreate ? 3 : undefined }}
            divider={true}
            secondaryAction={
              loading ? (
                <CircularProgress />
              ) : (
                <>
                  <IconButton onClick={submitForm}>
                    <Check color="success" />
                  </IconButton>
                  <IconButton onClick={onClose} edge="end">
                    <Close color="error" />
                  </IconButton>
                </>
              )
            }
          >
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: isProjektleiter ? 'secondary.main' : undefined }}>
                {isProjektleiter ? (
                  <Engineering sx={{ fontSize: '1.9rem' }} />
                ) : (
                  <Person sx={{ fontSize: '2.0rem' }} />
                )}
              </Avatar>
            </ListItemAvatar>
            <Box sx={{ pl: 4, display: 'flex', flexDirection: 'row' }}>
              <Box
                sx={{
                  width: 224,
                  mr: 2,
                }}
              >
                {modeIsCreate ? (
                  isIntern ? (
                    <MitarbeiterLink id={currentUserId ?? ''} label="Mitarbeiter" />
                  ) : (
                    <FormikMitarbeiterAutocomplete
                      variant="standard"
                      name="mitarbeiterId"
                      label="Mitarbeiter"
                      excludeIds={excludeMitarbeiterIdsFromAutocomplete}
                    />
                  )
                ) : (
                  <MitarbeiterLink
                    id={updateProjektMitarbeiterDTOinitialValues?.mitarbeiterId ?? ''}
                    label={isProjektleiter ? 'Projektleiter' : 'Mitarbeiter'}
                  />
                )}
              </Box>
              {!istPauschal && (
                <FormikEuroInput
                  name="stundenSatzInEuroCent"
                  textFieldProps={{
                    variant: 'standard',
                    label: 'Stundensatz',
                    fullWidth: true,
                    required: true,
                  }}
                />
              )}
            </Box>
          </ListItem>
        </Form>
      )}
    </Formik>
  );
};
